import { zipSync } from 'fflate';

import { getBlobAccessUrl } from '@/hooks/useBlobAccessUrl';

export async function fetchImageBlob(
	stableUrl: string,
	userId: string | null | undefined,
): Promise<Blob> {
	const accessUrl = await getBlobAccessUrl(stableUrl, userId);
	const res = await fetch(accessUrl);
	if (!res.ok) {
		throw new Error(`Download failed with status ${res.status}`);
	}
	return res.blob();
}

/**
 * Stored images may be jpg/webp (re-hosted or user-uploaded), but downloads
 * are always delivered as PNG. Re-encodes via canvas at the image's native
 * resolution; PNG inputs pass through untouched.
 */
export async function toPngBlob(blob: Blob): Promise<Blob> {
	if (blob.type === 'image/png') return blob;
	const bitmap = await createImageBitmap(blob);
	const canvas = document.createElement('canvas');
	canvas.width = bitmap.width;
	canvas.height = bitmap.height;
	const ctx = canvas.getContext('2d');
	if (!ctx) {
		throw new Error('Failed to get canvas context');
	}
	ctx.drawImage(bitmap, 0, 0);
	bitmap.close();
	return new Promise((resolve, reject) => {
		canvas.toBlob((png) => {
			if (png) {
				resolve(png);
			} else {
				reject(new Error('Failed to convert image to PNG'));
			}
		}, 'image/png');
	});
}

/**
 * Every downloaded file gets a unique name: bridgeway_<position>_<8-hex>.<ext>.
 * Position is the 1-based slide position (1 for single-image downloads), so
 * batch names stay unique and sort in slide order; the short random id keeps
 * repeat downloads from colliding on a user's disk.
 */
export function downloadFilename(position: number, ext: string): string {
	return `bridgeway_${position}_${crypto.randomUUID().slice(0, 8)}.${ext}`;
}

export function saveBlob(blob: Blob, filename: string): void {
	const objectUrl = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = objectUrl;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(objectUrl);
}

export async function downloadImageAsPng(
	stableUrl: string,
	userId: string | null | undefined,
	filename: string,
): Promise<void> {
	const blob = await fetchImageBlob(stableUrl, userId);
	saveBlob(await toPngBlob(blob), filename);
}

export function buildZipBlob(
	entries: Array<{ name: string; data: Uint8Array }>,
): Blob {
	const files: Record<string, Uint8Array> = {};
	for (const entry of entries) {
		files[entry.name] = entry.data;
	}
	// PNG data is already compressed; deflating it again costs time for no
	// size win, so store entries as-is.
	const zipped = zipSync(files, { level: 0 });
	return new Blob([zipped], { type: 'application/zip' });
}
